/**
 * Test ID utilities for DataTable components
 *
 * Pattern: All test IDs are generated via named setter functions.
 * This ensures consistency between components and tests.
 *
 * Copied from client/src/lib/util.ts - will be moved to shared folder later
 */

import { generateTestId } from "@client-shared";

// --- DataTable Main Component ---
export const setDataTableContainerTestId = () => generateTestId("DataTable", "Container");

// --- Filter Bar ---
export const setFilterBarTestId = () => generateTestId("DataTable", "FilterBar");
export const setSearchInputTestId = () => generateTestId("DataTable", "SearchInput");
export const setSearchClearButtonTestId = () => generateTestId("DataTable", "SearchClearButton");
export const setFilterButtonTestId = (index: number) => generateTestId("DataTable", "FilterButton", index);
export const setFilterGroupTestId = (groupId: string) => generateTestId("DataTable", "FilterGroup", groupId);
export const setFilterGroupOptionTestId = (groupId: string, value: string) =>
  generateTestId("DataTable", "FilterGroupOption", `${groupId}-${value}`);
export const setFilterBadgeTestId = (label: string) => generateTestId("DataTable", "FilterBadge", label);
export const setExportButtonTestId = () => generateTestId("DataTable", "ExportButton");
export const setClearFiltersButtonTestId = () => generateTestId("DataTable", "ClearFiltersButton");
export const setPromotedActionTestId = (label: string) => generateTestId("DataTable", "PromotedAction", label);
export const setColumnVisibilityTriggerTestId = () => generateTestId("DataTable", "ColumnVisibilityTrigger");
export const setColumnVisibilityContentTestId = () => generateTestId("DataTable", "ColumnVisibilityContent");
export const setColumnVisibilityItemTestId = (columnId: string) => generateTestId("DataTable", "ColumnVisibilityItem", columnId);
export const setColumnVisibilityResetTestId = () => generateTestId("DataTable", "ColumnVisibilityReset");

// --- Header ---
export const setHeaderTestId = () => generateTestId("DataTable", "Header");
export const setHeaderRowTestId = () => generateTestId("DataTable", "HeaderRow");
export const setHeaderColumnTestId = (id: string) => generateTestId("DataTable", "HeaderColumn", id);
export const setHeaderSortButtonTestId = (id: string) => generateTestId("DataTable", "HeaderSortButton", id);
export const setSelectAllCheckboxTestId = () => generateTestId("DataTable", "SelectAllCheckbox");
/** Tinted primary when unselected, solid primary when selected, so the toggle state is visible at a glance. */
export const getSelectionButtonToneClass = (isSelected: boolean) =>
  isSelected
    ? "cursor-pointer border border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
    : "cursor-pointer border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary";

export const setSelectAllButtonTestId = () => generateTestId("DataTable", "SelectAllButton");
export const setSelectAllRowTestId = () => generateTestId("DataTable", "SelectAllRow");

// --- Body / Rows ---
export const setBodyTestId = () => generateTestId("DataTable", "Body");
export const setRowTestId = (id: string) => generateTestId("DataTable", "Row", id);
export const setRowCheckboxTestId = (id: string) => generateTestId("DataTable", "RowCheckbox", id);
export const setGroupSelectButtonTestId = (id: string) => generateTestId("DataTable", "GroupSelectButton", id);
export const setCellTestId = (rowId: string, columnId: string) => generateTestId("DataTable", `Cell-${rowId}`, columnId);
export const setExpanderButtonTestId = (id: string) => generateTestId("DataTable", "ExpanderButton", id);

// --- Empty/Loading/Error States ---
export const setEmptyStateTestId = () => generateTestId("DataTable", "EmptyState");
export const setLoadingStateTestId = () => generateTestId("DataTable", "LoadingState");
export const setErrorStateTestId = () => generateTestId("DataTable", "ErrorState");
export const setRetryButtonTestId = () => generateTestId("DataTable", "RetryButton");
export const setLoadingRowTestId = (index: number) => generateTestId("DataTable", "LoadingRow", index);

// --- Bulk Actions ---
export const setBulkActionsBarTestId = () => generateTestId("DataTable", "BulkActionsBar");
export const setBulkActionButtonTestId = (label: string) => generateTestId("DataTable", "BulkActionButton", label);
export const setSelectionCountTestId = () => generateTestId("DataTable", "SelectionCount");
export const setClearSelectionButtonTestId = () => generateTestId("DataTable", "ClearSelectionButton");

// --- Pagination ---
export const setPaginationTestId = () => generateTestId("DataTable", "Pagination");
export const setPageSizeSelectTestId = () => generateTestId("DataTable", "PageSizeSelect");
export const setFirstPageButtonTestId = () => generateTestId("DataTable", "FirstPageButton");
export const setPrevPageButtonTestId = () => generateTestId("DataTable", "PrevPageButton");
export const setNextPageButtonTestId = () => generateTestId("DataTable", "NextPageButton");
export const setLastPageButtonTestId = () => generateTestId("DataTable", "LastPageButton");
export const setPageIndicatorTestId = () => generateTestId("DataTable", "PageIndicator");
export const setRowCountTestId = () => generateTestId("DataTable", "RowCount");
